/**
 * Referral Program API Client
 *
 * TypeScript client for Partner & Referral Program API endpoints.
 */

import { apiClient } from '../api-client';

// ============================================================================
// TYPES
// ============================================================================

export interface ReferralLink {
  id: string;
  workspace_id: string;
  code: string;
  uuid: string;
  url: string;
  link_type: "PARTNER" | "REFERRAL";
  clicks: number;
  conversions: number;
  created_at: string;
}

export interface ReferralStats {
  total_clicks: number;
  total_conversions: number;
  total_earned: number;
  pending_earnings: number;
  earnings_by_type: {
    REFERRAL_BONUS?: number;
    CREDIT_KICKBACK?: number;
    BILLING_COMMISSION?: number;
    TEMPLATE_USAGE?: number;
  };
}

export interface ReferralConfig {
  workspace_id: string;
  referral_enabled: boolean;
  referral_reward_amount: number;
  referral_min_purchase: number;
  referral_new_user_bonus?: number;
  partner_enabled: boolean;
  partner_billing_commission_rate: number;
  partner_credit_kickback_rate: number;
  partner_credit_threshold: number;
}

export interface ReferralCampaign {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  referral_reward_amount?: number;
  referral_min_purchase?: number;
  referral_new_user_bonus?: number;
  active: boolean;
  created_at: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const API_BASE = "/api/v2/referral";

/**
 * Create new referral or partner link
 */
export async function createReferralLink(
  linkType: "PARTNER" | "REFERRAL"
): Promise<{ link: ReferralLink; url: string }> {
  const response = await apiClient.post<{ link: ReferralLink; url: string }>(
    `${API_BASE}/links/create`,
    { link_type: linkType }
  );
  return response.data;
}

/**
 * Get all referral links for workspace
 */
export async function getReferralLinks(): Promise<{ links: ReferralLink[] }> {
  const response = await apiClient.get<{ links: ReferralLink[] }>(`${API_BASE}/links`);
  return response.data;
}

/**
 * Get referral statistics for dashboard
 */
export async function getReferralStats(): Promise<{ stats: ReferralStats }> {
  const response = await apiClient.get<{ stats: ReferralStats }>(`${API_BASE}/stats`);
  return response.data;
}

/**
 * Get referral config (Admin only)
 */
export async function getReferralConfig(): Promise<{ config: ReferralConfig }> {
  const response = await apiClient.get<{ config: ReferralConfig }>(`${API_BASE}/config`);
  return response.data;
}

/**
 * Update referral config (Admin only, SEC-001 validated)
 */
export async function updateReferralConfig(
  config: Partial<ReferralConfig>
): Promise<{ config: ReferralConfig }> {
  const response = await apiClient.put<{ config: ReferralConfig }>(`${API_BASE}/config`, config);
  return response.data;
}

/**
 * Create referral campaign (Admin only)
 */
export async function createReferralCampaign(
  campaign: Omit<ReferralCampaign, "id" | "workspace_id" | "created_at">
): Promise<{ campaign: ReferralCampaign }> {
  const response = await apiClient.post<{ campaign: ReferralCampaign }>(`${API_BASE}/campaigns`, campaign);
  return response.data;
}
